import type { Annotation, AnnotationEndpointStyle, AnnotationLayer } from './model';

export type AnnotationLegendSymbol =
  | {
      readonly kind: 'marker';
      readonly shape: import('../symbols/model').SymbolGraphic<'circle' | 'diamond' | 'square'>;
      readonly tint?: string | null;
      readonly backing?: import('../symbols/backing').SymbolBacking;
      readonly rotationDegrees?: number;
      readonly anchor?: 'center' | 'bottom';
      readonly fillColour: string;
      readonly outlineColour: string;
      readonly size: number;
      readonly opacity: number;
    }
  | {
      readonly kind: 'line';
      readonly colour: string;
      readonly width: number;
      readonly opacity: number;
      readonly dash: 'dashed' | 'solid';
      readonly endStyle: AnnotationEndpointStyle;
    }
  | {
      readonly kind: 'area';
      readonly shape: 'ellipse' | 'polygon' | 'rectangle';
      readonly fillColour: string;
      readonly fillOpacity: number;
      readonly outlineColour: string;
      readonly outlineWidth: number;
      readonly outlineDash: 'dashed' | 'solid';
      readonly cornerRadius: number;
    }
  | {
      readonly kind: 'focus-area';
      readonly fadeColour: string;
      readonly fadeOpacity: number;
      readonly fadeDirection: 'inside' | 'outside';
      readonly boundaryVisible: boolean;
      readonly boundaryColour: string;
      readonly boundaryWidth: number;
      readonly boundaryDash: 'dashed' | 'solid';
    };

export type AnnotationLayerLegendEligibility =
  | { readonly eligible: true; readonly symbol: AnnotationLegendSymbol }
  | {
      readonly eligible: false;
      readonly reason: 'empty' | 'mixed-types' | 'different-styles' | 'unsupported-type';
    };

export function annotationLegendSymbol(annotation: Annotation): AnnotationLegendSymbol | null {
  switch (annotation.type) {
    case 'marker':
      return { kind: 'marker', ...annotation.style };
    case 'line':
      return { kind: 'line', ...annotation.style };
    case 'polygon':
    case 'ellipse':
      return {
        kind: 'area',
        shape: annotation.type,
        ...annotation.style,
        cornerRadius: 0,
      };
    case 'rectangle':
      return { kind: 'area', shape: 'rectangle', ...annotation.style };
    case 'focus-area':
      return {
        kind: 'focus-area',
        fadeColour: annotation.style.fadeColour,
        fadeOpacity: annotation.style.fadeOpacity,
        fadeDirection: annotation.style.fadeDirection,
        boundaryVisible: annotation.style.boundaryVisible,
        boundaryColour: annotation.style.boundaryColour,
        boundaryWidth: annotation.style.boundaryWidth,
        boundaryDash: annotation.style.boundaryDash,
      };
    case 'text':
    case 'callout':
      return null;
  }
}

function symbolSignature(symbol: AnnotationLegendSymbol): string {
  if (symbol.kind === 'focus-area' && !symbol.boundaryVisible)
    return JSON.stringify({
      kind: symbol.kind,
      fadeColour: symbol.fadeColour,
      fadeOpacity: symbol.fadeOpacity,
      fadeDirection: symbol.fadeDirection,
      boundaryVisible: false,
    });
  return JSON.stringify(symbol);
}

export function annotationLayerLegendEligibility(
  layer: AnnotationLayer,
): AnnotationLayerLegendEligibility {
  if (!layer.annotations.length) return { eligible: false, reason: 'empty' };
  const firstAnnotation = layer.annotations[0]!;
  const first = annotationLegendSymbol(firstAnnotation);
  if (!first) return { eligible: false, reason: 'unsupported-type' };
  const firstSignature = symbolSignature(first);
  for (const annotation of layer.annotations.slice(1)) {
    const symbol = annotationLegendSymbol(annotation);
    if (!symbol) return { eligible: false, reason: 'unsupported-type' };
    if (annotation.type !== firstAnnotation.type) return { eligible: false, reason: 'mixed-types' };
    if (symbolSignature(symbol) !== firstSignature)
      return { eligible: false, reason: 'different-styles' };
  }
  return { eligible: true, symbol: first };
}

export function reconcileAnnotationLayerLegend(layer: AnnotationLayer): AnnotationLayer {
  if (!layer.includeInLegend || annotationLayerLegendEligibility(layer).eligible) return layer;
  return { ...layer, includeInLegend: false };
}

export function effectiveAnnotationLegendLabel(
  item: Pick<Annotation | AnnotationLayer, 'legendLabel' | 'name'>,
): string {
  return item.legendLabel?.trim() || item.name;
}

export function annotationLayerLegendEligibilityMessage(
  eligibility: Exclude<AnnotationLayerLegendEligibility, { eligible: true }>,
): string {
  switch (eligibility.reason) {
    case 'empty':
      return 'Add an annotation before adding this layer to the legend.';
    case 'mixed-types':
      return 'This layer contains multiple annotation symbol types.';
    case 'different-styles':
      return 'Annotations in this layer use different styles.';
    case 'unsupported-type':
      return 'This annotation type does not support a legend symbol.';
  }
}
